"use client";

import { Sparkles } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const stages = [{
  number: "01",
  title: "Signal Acquisition",
  description: "Initial orbital sweep to identify target signatures. High-fidelity sensor calibration ensures data integrity from the first millisecond."
}, {
  number: "02",
  title: "Telemetry Sync",
  description: "Establishing secure uplink channels. All incoming data streams are verified against baseline orbital parameters for precision."
}, {
  number: "03",
  title: "Noise Filtering",
  description: "Advanced algorithmic suppression of background radiation. We isolate the signal from the void to ensure absolute clarity."
}, {
  number: "04",
  title: "Vector Analysis",
  description: "Calculating trajectory and velocity of detected anomalies. Every movement is mapped within our proprietary intelligence grid."
}, {
  number: "05",
  title: "Pattern Recognition",
  description: "Cross-referencing detected signatures against our historical database. Identifying intent through behavioral modeling."
}, {
  number: "06",
  title: "Threat Assessment",
  description: "Quantifying risk levels based on proximity and signal strength. Automated alerts trigger when thresholds are breached."
}, {
  number: "07",
  title: "Data Normalization",
  description: "Converting raw sensor input into actionable intelligence. Standardizing formats for seamless integration into command systems."
}, {
  number: "08",
  title: "Encryption Handshake",
  description: "Securing the data pipeline with military-grade protocols. Ensuring that intelligence remains exclusive to authorized command nodes."
}, {
  number: "09",
  title: "Baseline Calibration",
  description: "Digital verification of sensor health across the entire array. Maintaining peak operational readiness for long-duration missions."
}, {
  number: "10",
  title: "Simulation Testing",
  description: "Running controlled scenarios to validate response protocols. Ensuring the system performs under extreme orbital conditions."
}, {
  number: "11",
  title: "Deep Defect Correction",
  description: "Removing anomalies in the data stream that could lead to false positives. Precision tuning of the detection engine."
}, {
  number: "12",
  title: "Refinement Processing",
  description: "Progressive stages of data smoothing to eliminate jitter. Intelligence clarity reaches near-optical transparency."
}, {
  number: "13",
  title: "Final Synthesis",
  description: "Consolidating all processed data into a unified mission brief. The intelligence picture becomes absolute."
}, {
  number: "14",
  title: "Forensic Inspection",
  description: "Scrutinizing the final output under simulated high-load conditions. Every data point verified from multiple vectors."
}, {
  number: "15",
  title: "System Wipe-Down",
  description: "Clearing temporary cache buffers to ensure the system is ready for the next high-priority mission cycle."
}, {
  number: "16",
  title: "Deployment Protocol",
  description: "Finalizing the uplink to the command center. The intelligence is now live and ready for strategic decision-making."
}, {
  number: "17",
  title: "Cure & Levelling",
  description: "Stabilizing the data output for long-term archival. Patience in processing yields the deepest strategic insights."
}, {
  number: "18",
  title: "Hardware Restoration",
  description: "Physical maintenance of sensor arrays, ensuring original performance and locking out environmental degradation."
}, {
  number: "19",
  title: "Remote System Calibratiog",
  description: "Remote telemetry-driven calibration to maximize sensor performance and signal capture."
}, {
  number: "20",
  title: "Interior Diagnostics",
  description: "Full system health check, internal cooling optimization, and firmware hardening. The unit is restored to factory-fresh precision."
}, {
  number: "21",
  title: "Final Quality Control",
  description: "Multi-point inspection under varied orbital lighting conditions. Nothing leaves the facility until it meets the TiaLinx Standard."
}];
export default function OperationalWorkflow() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: spineRef,
    isVisible: spineVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.05
  });
  const {
    ref: finaleRef,
    isVisible: finaleVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="2689" id="operational-workflow" className="relative bg-[#080F1A] text-[#B2BDC7] overflow-hidden py-24 md:py-32 lg:py-40 px-4 sm:px-6 lg:px-8">
      <div className="pointer-events-none absolute inset-0 opacity-[0.03] mix-blend-overlay" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")"
    }} />

      <div className="relative max-w-7xl mx-auto">
        <div ref={headerRef} className={`text-center mb-20 md:mb-28 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="font-mono text-xs md:text-sm tracking-[0.4em] text-[#45596F] uppercase mb-6">MISSION QUALITY CONTROL</p>
          <h2 className="font-default font-semibold text-5xl sm:text-6xl md:text-7xl lg:text-[64px] leading-[1.05] tracking-tight text-[#B2BDC7]">The 21-stage System&nbsp;intelligence.</h2>
          <p className="mt-8 max-w-2xl mx-auto text-base md:text-lg text-[#45596F] leading-relaxed">
            A precise, repeatable system engineered for orbital dominance. Every stage is critical to the mission. No data point is left to chance.
          </p>
        </div>

        <div ref={spineRef} className="relative">
          <div className="absolute left-6 md:left-1/2 top-0 bottom-0 -translate-x-1/2 w-px overflow-hidden">
            <div className="absolute inset-0 bg-[linear-gradient(180deg,transparent_0%,#45596F_5%,#6697B9_50%,#45596F_95%,transparent_100%)]" />
            <div className="absolute top-0 left-0 right-0 bg-[linear-gradient(180deg,#B2BDC7_0%,#6697B9_50%,#B2BDC7_100%)] transition-all duration-[2000ms] ease-out" style={{
            height: spineVisible ? "100%" : "0%",
            boxShadow: "0 0 8px #6697B9"
          }} />
          </div>

          <div className="space-y-16 md:space-y-24">
            {stages.map((stage, i) => <StageEntry key={stage.number} data-index={i} number={stage.number} title={stage.title} description={stage.description} index={i} />)}
          </div>
        </div>

        <div ref={finaleRef} className={`relative mt-24 md:mt-32 text-center transition-all duration-1000 ease-out ${finaleVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="absolute left-6 md:left-1/2 -top-16 md:-top-20 -translate-x-1/2 flex items-center justify-center">
            <div className="w-4 h-4 rounded-lg bg-[linear-gradient(135deg,#B2BDC7_0%,#6697B9_100%)] ring-4 ring-[#080F1A] relative">
              <div className="absolute inset-0 rounded-lg blur-md bg-[#6697B9]/40" />
            </div>
          </div>

          <div className="inline-flex items-center gap-3 mb-4 justify-center">
            <Sparkles className="w-5 h-5 text-[#6697B9]" />
            <span className="font-mono text-xs tracking-[0.4em] uppercase text-[#45596F]">
              Status
            </span>
            <Sparkles className="w-5 h-5 text-[#6697B9]" />
          </div>
          <h3 className="font-default font-semibold italic text-3xl md:text-4xl lg:text-[36px] leading-tight bg-clip-text text-transparent bg-[linear-gradient(135deg,#B2BDC7_0%,#6697B9_30%,#B2BDC7_50%,#507697_75%,#B2BDC7_100%)]" style={{
          textShadow: "0 0 40px #6697B9 / 0.15"
        }}>
            Command Ready
          </h3>
        </div>
      </div>
    </section>;
}
function StageEntry({
  number,
  title,
  description,
  index,
  ...rest
}: {
  number: string;
  title: string;
  description: string;
  index: number;
  [key: string]: unknown;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const isLeft = index % 2 === 0;
  const delay = `${index % 10 * 100}ms`;
  return <div ref={ref} {...rest} className={`relative grid grid-cols-[3rem_1fr] md:grid-cols-2 md:gap-16 lg:gap-24 items-center transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
    transitionDelay: delay
  }}>
      <div className="absolute left-6 md:left-1/2 top-8 md:top-1/2 -translate-x-1/2 md:-translate-y-1/2 z-10">
        <div className="relative w-3 h-3">
          <div className="absolute inset-0 rounded-lg bg-[linear-gradient(135deg,#B2BDC7_0%,#6697B9_100%)] ring-[3px] ring-[#080F1A]" />
          <div className={`absolute inset-0 rounded-lg blur-sm bg-[#6697B9]/50 transition-opacity duration-700 ${isVisible ? "opacity-100" : "opacity-0"}`} style={{
          transitionDelay: delay
        }} />
        </div>
      </div>

      <div className={`pl-12 md:pl-0 ${isLeft ? "md:text-right md:pr-4" : "md:order-2 md:text-left md:pl-4"}`}>
        <div className={`flex items-baseline gap-4 mb-3 ${isLeft ? "md:justify-end" : "md:justify-start"}`}>
          
        </div>
        <h3 className="font-default font-semibold text-xl md:text-2xl text-[#B2BDC7] mb-3 leading-tight">
          {title}
        </h3>
        <p className="text-[15px] leading-relaxed text-[#45596F] max-w-md md:inline-block">
          {description}
        </p>
      </div>

      <div className={`hidden md:block ${isLeft ? "md:order-2" : ""}`} aria-hidden="true" />
    </div>;
}